import React, { useState, useEffect } from "react";

import Select from "react-select";

import PropTypes from "prop-types";

import { format } from "date-fns";

import { useQuery } from "@apollo/client";

import { Title, Button, Field, Control, Label, Content } from "rbx";
import { initialValue } from "rbx/components/dropdown/dropdown-context";

import { DateInput, Loader } from "../../../../components";
import { FIND_MANY_CODES } from "../../../../graphql/codes/codes.queries";
import { stripTimeZone } from "../../../../utils";

const DeccoUForm = ({ onComplete, onCancel, initialInputs = {}, employeeData = {}, mobile  }) => {
  const [inputs, setInputs] = useState({});
  const [employeeCourses, setEmployeeCourses] = useState([]);
  const [repeatCourse, setRepeatCourse] = useState(null);
  const { data: { findManyCodes: options } = {}, loading } = useQuery(
    FIND_MANY_CODES,
    {
      variables: {
        where: {
          entity: {
            equals: "deccoUCourses",
          },
        },
        orderBy: [
          {
            title: "asc",
          },
        ],
      },
    }
  );


  useEffect(() => {
    if (employeeData?.deccoUCourses?.length > 0) {
      const courseIds = employeeData?.deccoUCourses?.map((s) => ({courseId: s?.id, codeId: s?.codes?.id}));
      setEmployeeCourses(courseIds);
    }
  }, [employeeData]);

  useEffect(() => {
    if (inputs?.value && employeeCourses?.length > 0) {
      const oldCourse = employeeCourses.find((s) => (s?.codeId === inputs?.value));
      if (oldCourse) {
        setRepeatCourse(oldCourse?.courseId);
      } else {
        setRepeatCourse(null);
      }
    }
  }, [inputs, inputs.value, employeeCourses]);

  useEffect(() => {
    if (initialInputs?.id) {
      setInputs({
        expDate: initialInputs?.expDate,
        issueDate: initialInputs?.issueDate,
        title: initialInputs?.title || "",
        expirationType: initialInputs?.expirationType || "manual",
      });
    }
  }, [initialInputs]);

  const handleChange = (e) => {
    const name = e?.target?.name || e?.name;
    const value = e?.target?.value || e?.value;

    setInputs({ ...inputs, [name]: value });
  };

  const handleDeccoUCourseChange = (e) => {
    setInputs({ ...inputs, ...e });
  };

  const handleCreateNewDeccoUCourse = () => {

     if(repeatCourse) {
        // treat as update
      const updateData = {
        deccoUCourses: {
          update: [
            {
              data: {
                issueDate: {
                  set: inputs?.issueDate || format(new Date(), "yyyy-MM-dd"),
                },
              },
              where: {
                id: repeatCourse,
              },
            },
          ],
        },
      };
      if (inputs?.expirationType === "manual") {
        updateData.deccoUCourses.update[0].expDate = {
          set: inputs?.expDate || format(new Date(), "yyyy-MM-dd"),
        };
      }
      return updateData;
    } 
    const data = {
      deccoUCourses: {
        create: [
          {
            codes: {
              connect: {
                id: inputs?.value,
              },
            },
            issueDate: inputs?.issueDate || format(new Date(), "yyyy-MM-dd"),
          },
        ],
      },
    };

    if (inputs?.expirationType === "manual") {
      data.deccoUCourses.create[0].expDate =
        inputs?.expDate || format(new Date(), "yyyy-MM-dd");
    }
    return data;
  };

  const handleUpdateDeccoUCourse = () => {
    const d = {
      deccoUCourses: {
        update: [
          {
            data: {
              issueDate: {
                set:
                  stripTimeZone(inputs?.issueDate) ||
                  format(new Date(), "yyyy-MM-dd"),
              },
            },
            where: {
              id: initialInputs?.id,
            },
          },
        ],
      },
    };

    if (inputs?.expirationType === "manual") {
      d.deccoUCourses.update[0].data.expDate = {
        set: inputs?.expDate || format(new Date(), "yyyy-MM-dd"),
      };
    }

    return d;
  };

  const handleComplete = () => {
    if (initialInputs?.id) {
      onComplete(handleUpdateDeccoUCourse());
    } else {
      onComplete(handleCreateNewDeccoUCourse());
    }
  };

  if (loading && options?.length) return <Loader />;

  return (
    <div
      id="DeccoUForm"
      style={{
        minHeight: "60vh",
        overflow: "auto",
      }}
    >
      <header className="modal-head">
        <div className="modal-head-start">
          <Title size={5}>
            {initialInputs?.id ? "Update" : "Create"} DECCOU Course
          </Title>
        </div>
        {mobile && (
          <br />
        )}
        <div className="modal-head-end">
          <Button.Group hasAddons>
            <Button
              disabled={loading}
              size="small"
              type="button"
              onClick={() => onCancel()}
            >
              <span>Cancel</span>
            </Button>
            <Button
              color="primary"
              disabled={!inputs?.value && !initialInputs?.id}
              form="company-form"
              size="small"
              onClick={handleComplete}
            >
              <span>Save</span>
            </Button>
          </Button.Group>
        </div>
      </header>
      <hr />
      <Field
        style={{
          display: "flex",
          flexDirection: "column",
          justifContent: "stretch",
          alignItems: "stretch",
        }}
      >
        <Control>
          <Label textSize={5}>Title:</Label>
          {initialInputs?.id ? (
            <Content size="medium">{`${inputs?.title}`}</Content>
          ) : (
            <div style={{ width: "100%" }}>
              {repeatCourse && (
                <Content size="medium" textColor="danger">
                  This course is already assigned to this employee. Adding another will replace the old course.
                </Content>
              )}
              <Select
                options={options?.map((o) => ({
                  value: o?.id,
                  label: `${o?.title}`,
                  ...o,
                }))}
                value={inputs || initialValue}
                onChange={(e) => {
                  handleDeccoUCourseChange(e);
                }}
              />
            </div>
          )}
        </Control>
        <Field
          style={{
            display: "flex",
            flexDirection: mobile ? "column": "row",
            justifyContent: "flex-start",
            gap: "1rem",
          }}
        >
          <Control>
            <Label textSize={5}>Issue Date:</Label>

            <DateInput
              name="issueDate"
              type="date"
              value={inputs?.issueDate || format(new Date(), "yyyy-MM-dd")}
              onChange={(e) => handleChange(e)}
            />
          </Control>

          {(inputs?.expirationType === "manual" ||
            inputs?.expirationType === "manual") && (
            <Control>
              <Label textSize={5}>Expiration Date:</Label>
              <DateInput
                name="expDate"
                type="date"
                value={inputs.expDate || format(new Date(), "yyyy-MM-dd")}
                onChange={(e) => handleChange(e)}
              />
            </Control>
          )}
        </Field>
      </Field>
    </div>
  );
};

DeccoUForm.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialInputs: PropTypes.object,
  employeeData: PropTypes.object,
  mobile: PropTypes.bool,
};

DeccoUForm.defaultProps = {
  initialInputs: {},
  employeeData: {},
  mobile: false,
};

export default DeccoUForm;
